/**
 * Animated liquid textures: every animation frame of water and lava packed
 * into one sampler2DArray, so the water shader picks a frame per time step
 * instead of the atlas being re-uploaded.
 *
 * Layers are [water frames..., lava frames...], each 16x16 RGBA8, sRGB.
 * Sources are vanilla animation strips (16 px wide, N square frames stacked
 * vertically). water_still is grayscale + alpha (tinted per biome in the
 * shader); lava_still is full colour and opaque. Missing files fall back to
 * procedural frames.
 *
 * Row 0 of each layer is the TOP row of the image (flipY stays false, same
 * convention as the atlas), so v=0 is the top.
 */

import * as THREE from 'three';
import { mulberry32 } from '../noise';

export interface LiquidTextures {
  /** Layers: [water frames..., lava frames...]. */
  texture: THREE.DataArrayTexture;
  /** >= 1; water layers are [0, waterFrames). */
  waterFrames: number;
  /** Index of the first lava layer (= waterFrames). */
  lavaStart: number;
  /** >= 1. */
  lavaFrames: number;
}

/** Layer edge length in texels. */
const SIZE = 16;
const LAYER_BYTES = SIZE * SIZE * 4;
const FALLBACK_FRAMES = 8;
const TAU = Math.PI * 2;

interface FrameSet {
  /** frames * LAYER_BYTES, rows top-down. */
  data: Uint8Array;
  frames: number;
}

async function loadImage(name: string): Promise<HTMLImageElement | null> {
  const img = new Image();
  img.src = `mc/${name}.png`; // relative — works under vite's './' base
  try {
    await img.decode();
    return img;
  } catch {
    return null; // not extracted — procedural fallback
  }
}

/** Slice a vertical animation strip into 16x16 frames (nearest-neighbour if the pack isn't 16px). */
function decodeStrip(img: HTMLImageElement): FrameSet | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const frames = w > 0 ? Math.floor(h / w) : 0;
  if (frames < 1) return null;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  const src = ctx.getImageData(0, 0, w, h).data;

  const data = new Uint8Array(frames * LAYER_BYTES);
  for (let f = 0; f < frames; f++) {
    for (let y = 0; y < SIZE; y++) {
      // Texel centres, so down- and up-sampling both pick a stable source texel.
      const sy = f * w + Math.min(w - 1, Math.floor(((y + 0.5) * w) / SIZE));
      for (let x = 0; x < SIZE; x++) {
        const sx = Math.min(w - 1, Math.floor(((x + 0.5) * w) / SIZE));
        const s = (sy * w + sx) * 4;
        const d = f * LAYER_BYTES + (y * SIZE + x) * 4;
        data[d] = src[s];
        data[d + 1] = src[s + 1];
        data[d + 2] = src[s + 2];
        data[d + 3] = src[s + 3];
      }
    }
  }
  return { data, frames };
}

interface Wave {
  kx: number;
  ky: number;
  /** Whole cycles per animation loop (signed), so the last frame flows into the first. */
  speed: number;
  phase: number;
  amp: number;
}

/**
 * Sum of plane waves with INTEGER wave numbers over the 16px tile: periodic in
 * x and y (tiles seamlessly) and in time (loops seamlessly).
 */
function makeWaves(rnd: () => number, count: number, maxK: number): Wave[] {
  const waves: Wave[] = [];
  for (let i = 0; i < count; i++) {
    let kx = 0;
    let ky = 0;
    while (kx === 0 && ky === 0) {
      kx = Math.round((rnd() * 2 - 1) * maxK);
      ky = Math.round((rnd() * 2 - 1) * maxK);
    }
    waves.push({
      kx,
      ky,
      speed: rnd() < 0.5 ? -1 : 1,
      phase: rnd() * TAU,
      amp: 0.5 + rnd() * 0.5,
    });
  }
  return waves;
}

/** Field value in [-1, 1] at texel (x, y), animation phase t in [0, 1). */
function sampleWaves(waves: Wave[], x: number, y: number, t: number): number {
  let sum = 0;
  let norm = 0;
  for (const w of waves) {
    sum += w.amp * Math.sin((TAU * (w.kx * x + w.ky * y)) / SIZE + TAU * w.speed * t + w.phase);
    norm += w.amp;
  }
  return sum / norm;
}

/** Grayscale ripples, value ~170..235, alpha ~0.75. */
function proceduralWater(): FrameSet {
  const rnd = mulberry32(0x57a7e2);
  const waves = makeWaves(rnd, 5, 2);
  // Static per-texel grain keeps the pixel-art look; fixed across frames so it doesn't flicker.
  const grain = new Float32Array(SIZE * SIZE);
  for (let i = 0; i < grain.length; i++) grain[i] = (rnd() - 0.5) * 10;

  const data = new Uint8Array(FALLBACK_FRAMES * LAYER_BYTES);
  for (let f = 0; f < FALLBACK_FRAMES; f++) {
    const t = f / FALLBACK_FRAMES;
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const n = sampleWaves(waves, x, y, t);
        const v = Math.max(170, Math.min(235, Math.round(202 + n * 28 + grain[y * SIZE + x])));
        const d = f * LAYER_BYTES + (y * SIZE + x) * 4;
        data[d] = v;
        data[d + 1] = v;
        data[d + 2] = v;
        data[d + 3] = 191;
      }
    }
  }
  return { data, frames: FALLBACK_FRAMES };
}

/** Opaque orange crust with slow yellow blotches. */
function proceduralLava(): FrameSet {
  const rnd = mulberry32(0x1a7a5eed);
  const waves = makeWaves(rnd, 4, 2);
  const grain = new Float32Array(SIZE * SIZE);
  for (let i = 0; i < grain.length; i++) grain[i] = (rnd() - 0.5) * 0.12;

  const data = new Uint8Array(FALLBACK_FRAMES * LAYER_BYTES);
  for (let f = 0; f < FALLBACK_FRAMES; f++) {
    const t = f / FALLBACK_FRAMES;
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        let h = sampleWaves(waves, x, y, t) * 0.5 + 0.5 + grain[y * SIZE + x];
        h = Math.max(0, Math.min(1, h));
        // Sharpen so hot spots read as blotches rather than a smooth gradient.
        h = h * h * (3 - 2 * h);
        const d = f * LAYER_BYTES + (y * SIZE + x) * 4;
        data[d] = Math.round(207 + h * 48); // 207..255
        data[d + 1] = Math.round(72 + h * 150); // 72..222
        data[d + 2] = Math.round(8 + h * 72); // 8..80
        data[d + 3] = 255;
      }
    }
  }
  return { data, frames: FALLBACK_FRAMES };
}

async function loadFrames(name: string, fallback: () => FrameSet): Promise<FrameSet> {
  const img = await loadImage(name);
  return (img && decodeStrip(img)) || fallback();
}

export async function buildLiquidTextures(): Promise<LiquidTextures> {
  const [water, lava] = await Promise.all([
    loadFrames('water_still', proceduralWater),
    loadFrames('lava_still', proceduralLava),
  ]);

  const layers = water.frames + lava.frames;
  const data = new Uint8Array(layers * LAYER_BYTES);
  data.set(water.data, 0);
  data.set(lava.data, water.frames * LAYER_BYTES);

  const texture = new THREE.DataArrayTexture(data, SIZE, SIZE, layers);
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.colorSpace = THREE.SRGBColorSpace; // -> SRGB8_ALPHA8, decoded to linear on sample
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true; // mips are per layer; frames never bleed into each other
  // flipY stays false: UNPACK_FLIP_Y is illegal for 3D/array uploads in WebGL2.
  texture.needsUpdate = true;

  return {
    texture,
    waterFrames: water.frames,
    lavaStart: water.frames,
    lavaFrames: lava.frames,
  };
}
